import React, { useState, useEffect } from 'react';
import { canAccess, isManager, supabase } from './supabase';
import { askAI } from './ai';
import { qboFetch } from './qbo';

const ENTITY_LABELS = {
  national:     'REDLINE National',
  florida:      'Fire Watch Tampa Bay',
  newjersey:    'Fire Watch NJ',
  academy:      'Fire Watch Academy',
  technologies: 'Fire Watch Technologies',
};

const ENTITY_COLORS = {
  national:     '#EF4444',
  florida:      '#3B82F6',
  newjersey:    '#22C55E',
  academy:      '#F59E0B',
  technologies: '#8B5CF6',
};

const PERIODS = [
  { label: 'This month',   key: 'this_month' },
  { label: 'Last 30 days', key: 'last_30' },
  { label: 'Last 90 days', key: 'last_90' },
  { label: 'This quarter', key: 'this_quarter' },
];

function getDateRange(periodKey) {
  const today = new Date();
  const fmt = d => d.toISOString().split('T')[0];
  if (periodKey === 'this_month') return { start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), end: fmt(today) };
  if (periodKey === 'last_30') { const s = new Date(today); s.setDate(s.getDate() - 30); return { start: fmt(s), end: fmt(today) }; }
  if (periodKey === 'last_90') { const s = new Date(today); s.setDate(s.getDate() - 90); return { start: fmt(s), end: fmt(today) }; }
  if (periodKey === 'this_quarter') { const q = Math.floor(today.getMonth() / 3); return { start: fmt(new Date(today.getFullYear(), q * 3, 1)), end: fmt(today) }; }
  return { start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), end: fmt(today) };
}

function fmt$(n) {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function parsePL(report) {
  if (!report || !report.Rows) return { revenue: 0, cogs: 0, grossProfit: 0, expenses: 0, netIncome: 0 };
  const rows = report.Rows.Row || [];
  const findGroup = g => rows.find(r => r.group === g);
  const val = row => parseFloat(row?.Summary?.ColData?.[1]?.value) || 0;
  return { revenue: val(findGroup('Income')), cogs: val(findGroup('COGS')), grossProfit: val(findGroup('GrossProfit')), expenses: val(findGroup('Expenses')), netIncome: val(findGroup('NetIncome')) };
}

function parseAR(report) {
  const empty = { current: 0, days1_30: 0, days31_60: 0, days61_90: 0, days90plus: 0, total: 0 };
  if (!report || !report.Rows) return empty;

  let current = 0, d1 = 0, d2 = 0, d3 = 0, d4 = 0;

  for (const section of (report.Rows.Row || [])) {
    const header = section.Header?.ColData?.[0]?.value || '';
    const summaryVal = parseFloat(section.Summary?.ColData?.[6]?.value) || 0;
    if (header.toLowerCase().includes('current')) current += summaryVal;
    else if (header.includes('1 - 30') || header.includes('1-30')) d1 += summaryVal;
    else if (header.includes('31 - 60') || header.includes('31-60')) d2 += summaryVal;
    else if (header.includes('61 - 90') || header.includes('61-90')) d3 += summaryVal;
    else if (header.includes('91') || header.includes('over')) d4 += summaryVal;
  }

  const total = current + d1 + d2 + d3 + d4;
  return { current, days1_30: d1, days31_60: d2, days61_90: d3, days90plus: d4, total };
}

export default function FinancialsPage({ profile }) {
  const [period, setPeriod] = useState('this_month');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [aiInsight, setAiInsight] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  // Entities where this person sees revenue and AR totals only (no costs, no customers).
  const [revenueOnly, setRevenueOnly] = useState([]);
  useEffect(() => {
    supabase.from('profile_revenue_only').select('entity').eq('profile_id', profile.id)
      .then(({ data }) => setRevenueOnly((data || []).map(r => r.entity)), () => {});
  }, [profile.id]);
  const accessibleEntities = Object.keys(ENTITY_LABELS).filter(e => canAccess(profile, e) || revenueOnly.includes(e));

  async function fetchFinancials() {
    setLoading(true);
    setLoaded(false);
    setAiInsight('');
    const { start, end } = getDateRange(period);

    try {
      const res = await qboFetch('/qbo/reports', {
        method: 'POST',
        body: JSON.stringify({ entities: accessibleEntities, start_date: start, end_date: end })
      });
      const json = await res.json();
      const parsed = {};
      for (const entity of accessibleEntities) {
        const d = json.results?.[entity];
        if (!d || d.error) { parsed[entity] = { error: d?.error || 'No data' }; continue; }
        parsed[entity] = { pl: parsePL(d.pl), ar: parseAR(d.ar), connected: true, revenueOnly: !!d.revenue_only };
      }
      setData(parsed);
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
    setLoaded(true);
  }

  async function getAiInsights() {
    setAiLoading(true);
    setAiInsight('');
    const { start, end } = getDateRange(period);
    const summary = accessibleEntities.map(e => {
      const d = data[e];
      if (!d || d.error) return `${ENTITY_LABELS[e]}: not connected`;
      if (d.revenueOnly) return `${ENTITY_LABELS[e]}: Revenue ${fmt$(d.pl.revenue)} (costs and profit are not shared for this entity — do not infer them). AR total ${fmt$(d.ar.total)}.`;
      return `${ENTITY_LABELS[e]}: Revenue ${fmt$(d.pl.revenue)}, COGS ${fmt$(d.pl.cogs)}, Gross Profit ${fmt$(d.pl.grossProfit)}, Expenses ${fmt$(d.pl.expenses)}, Net Income ${fmt$(d.pl.netIncome)}. AR: Current ${fmt$(d.ar.current)}, 1-30 days ${fmt$(d.ar.days1_30)}, 31-60 days ${fmt$(d.ar.days31_60)}, 61-90 days ${fmt$(d.ar.days61_90)}, 90+ days ${fmt$(d.ar.days90plus)}.`;
    }).join('\n');

    try {
      const prompt = `You are a financial advisor analyzing a fire watch services company portfolio. Financial data for ${start} to ${end}:\n\n${summary}\n\nProvide 4-6 specific, actionable insights about this portfolio's financial health. Focus on: profitability concerns, AR collection issues, entity performance comparisons, and specific recommendations to improve margins or cash flow. Be direct and specific with numbers.`;
      setAiInsight(await askAI(prompt, 3000));
    } catch (err) {
      setAiInsight('Error: ' + err.message);
    }
    setAiLoading(false);
  }

  const rollup = accessibleEntities.reduce((acc, e) => {
    const d = data[e];
    if (!d || d.error || !d.pl) return acc;
    if (d.revenueOnly) return { ...acc, revenue: acc.revenue + d.pl.revenue, arTotal: acc.arTotal + d.ar.total, ar90plus: acc.ar90plus + d.ar.days90plus };
    return { revenue: acc.revenue + d.pl.revenue, cogs: acc.cogs + d.pl.cogs, grossProfit: acc.grossProfit + d.pl.grossProfit, expenses: acc.expenses + d.pl.expenses, netIncome: acc.netIncome + d.pl.netIncome, arTotal: acc.arTotal + d.ar.total, ar90plus: acc.ar90plus + d.ar.days90plus };
  }, { revenue: 0, cogs: 0, grossProfit: 0, expenses: 0, netIncome: 0, arTotal: 0, ar90plus: 0 });

  const costRevenue = accessibleEntities.reduce((sum, e) => (data[e] && data[e].pl && !data[e].revenueOnly ? sum + data[e].pl.revenue : sum), 0);
  const marginPct = costRevenue > 0 ? ((rollup.grossProfit / costRevenue) * 100).toFixed(1) : '—';
  const netPct = costRevenue > 0 ? ((rollup.netIncome / costRevenue) * 100).toFixed(1) : '—';
  const hasRevenueOnly = accessibleEntities.some(e => data[e] && data[e].revenueOnly);

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        {PERIODS.map(p => (
          <button key={p.key} onClick={() => { setPeriod(p.key); setLoaded(false); setAiInsight(''); }}
            className={'btn btn-sm ' + (period === p.key ? 'btn-primary' : 'btn-secondary')}>
            {p.label}
          </button>
        ))}
        <button onClick={fetchFinancials} disabled={loading} className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }}>
          {loading ? 'Loading…' : loaded ? '↻ Refresh' : 'Load financials'}
        </button>
      </div>

      {!loaded && !loading && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#94A3B8', fontSize: '13px', border: '1px dashed #E2E8F0', borderRadius: '10px' }}>
          Select a period and click "Load financials" to pull data from QuickBooks
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#94A3B8', fontSize: '13px' }}>
          Loading financial data from QuickBooks…
        </div>
      )}

      {loaded && !loading && (
        <>
          {/* Portfolio rollup */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Portfolio rollup</div>
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-label">Total revenue</div>
                <div className="metric-value blue">{fmt$(rollup.revenue)}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Gross profit</div>
                <div className="metric-value green">{fmt$(rollup.grossProfit)}</div>
                <div className="metric-sub">{marginPct}% margin{hasRevenueOnly ? ' · excludes revenue-only entities' : ''}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Net income</div>
                <div className="metric-value" style={{ color: rollup.netIncome >= 0 ? '#22C55E' : '#EF4444' }}>{fmt$(rollup.netIncome)}</div>
                <div className="metric-sub">{netPct}% margin</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Total AR</div>
                <div className="metric-value amber">{fmt$(rollup.arTotal)}</div>
                <div className="metric-sub" style={{ color: rollup.ar90plus > 0 ? '#EF4444' : '#94A3B8' }}>{fmt$(rollup.ar90plus)} 90+ days</div>
              </div>
            </div>
          </div>

          {/* Entity P&L cards */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>P&L by entity</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
              {accessibleEntities.map(entity => {
                const d = data[entity];
                const color = ENTITY_COLORS[entity];
                if (!d || d.error) return (
                  <div key={entity} style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '16px', borderLeft: `3px solid ${color}` }}>
                    <div style={{ fontWeight: '700', fontSize: '13px', marginBottom: '8px' }}>{ENTITY_LABELS[entity]}</div>
                    <div style={{ fontSize: '12px', color: '#94A3B8' }}>{d?.error || 'No data'}</div>
                  </div>
                );
                if (d.revenueOnly) return (
                  <div key={entity} style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '16px', borderLeft: `3px solid ${color}` }}>
                    <div style={{ fontWeight: '700', fontSize: '13px', marginBottom: '12px', color: '#0F172A' }}>{ENTITY_LABELS[entity]}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0' }}><span style={{ color: '#64748B' }}>Revenue</span><span style={{ fontWeight: '700' }}>{fmt$(d.pl.revenue)}</span></div>
                    <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '6px' }}>Costs and profit are not shared for this entity.</div>
                  </div>
                );
                const gm = d.pl.revenue > 0 ? ((d.pl.grossProfit / d.pl.revenue) * 100).toFixed(1) : '—';
                return (
                  <div key={entity} style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '16px', borderLeft: `3px solid ${color}` }}>
                    <div style={{ fontWeight: '700', fontSize: '13px', marginBottom: '12px', color: '#0F172A' }}>{ENTITY_LABELS[entity]}</div>
                    {[
                      { label: 'Revenue',      val: d.pl.revenue,     bold: false },
                      { label: 'COGS',         val: d.pl.cogs,        bold: false },
                      { label: 'Gross profit', val: d.pl.grossProfit, bold: true, sub: gm + '%' },
                      { label: 'Expenses',     val: d.pl.expenses,    bold: false },
                      { label: 'Net income',   val: d.pl.netIncome,   bold: true, color: d.pl.netIncome >= 0 ? '#22C55E' : '#EF4444' },
                    ].map(row => (
                      <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0', borderBottom: row.bold ? 'none' : '1px solid #F8FAFC' }}>
                        <span style={{ color: '#64748B', fontWeight: row.bold ? '600' : '400' }}>{row.label}</span>
                        <span style={{ fontWeight: row.bold ? '700' : '500', color: row.color || '#0F172A' }}>
                          {fmt$(row.val)}{row.sub ? <span style={{ fontSize: '10px', color: '#94A3B8', marginLeft: '4px' }}>{row.sub}</span> : null}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* AR Aging table */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>AR aging summary</div>
            <div style={{ border: '1px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                    {['Entity', 'Current', '1–30 days', '31–60 days', '61–90 days', '90+ days', 'Total'].map(h => (
                      <th key={h} style={{ padding: '9px 14px', textAlign: h === 'Entity' ? 'left' : 'right', fontWeight: '600', color: '#64748B', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {accessibleEntities.map((entity, i) => {
                    const d = data[entity];
                    const color = ENTITY_COLORS[entity];
                    if (!d || d.error || !d.ar) return (
                      <tr key={entity} style={{ borderBottom: i < accessibleEntities.length - 1 ? '1px solid #F8FAFC' : 'none' }}>
                        <td style={{ padding: '10px 14px', fontWeight: '600' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color }} />
                            {ENTITY_LABELS[entity]}
                          </div>
                        </td>
                        <td colSpan={6} style={{ padding: '10px 14px', color: '#94A3B8', textAlign: 'right' }}>{d?.error || '—'}</td>
                      </tr>
                    );
                    return (
                      <tr key={entity} style={{ borderBottom: i < accessibleEntities.length - 1 ? '1px solid #F8FAFC' : 'none' }}>
                        <td style={{ padding: '10px 14px', fontWeight: '600', color: '#0F172A' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color }} />
                            {ENTITY_LABELS[entity]}
                          </div>
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>{fmt$(d.ar.current)}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', color: '#F59E0B' }}>{fmt$(d.ar.days1_30)}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', color: '#F97316' }}>{fmt$(d.ar.days31_60)}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', color: '#EF4444' }}>{fmt$(d.ar.days61_90)}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', color: '#B91C1C', fontWeight: d.ar.days90plus > 0 ? '700' : '400' }}>{fmt$(d.ar.days90plus)}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: '700' }}>{fmt$(d.ar.total)}</td>
                      </tr>
                    );
                  })}
                  <tr style={{ background: '#F8FAFC', borderTop: '2px solid #E2E8F0' }}>
                    <td style={{ padding: '10px 14px', fontWeight: '700' }}>Total</td>
                    {['current','days1_30','days31_60','days61_90','days90plus','total'].map(k => (
                      <td key={k} style={{ padding: '10px 14px', textAlign: 'right', fontWeight: '700' }}>
                        {fmt$(accessibleEntities.reduce((sum, e) => sum + (data[e]?.ar?.[k] || 0), 0))}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* AI Insights */}
          <div style={{ border: '1px solid #E2E8F0', borderRadius: '10px', padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: aiInsight ? '16px' : '0' }}>
              <div>
                <div style={{ fontWeight: '700', fontSize: '13px', color: '#0F172A' }}>AI Portfolio Insights</div>
                <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '2px' }}>Claude analyzes your financial data and surfaces actionable recommendations</div>
              </div>
              <button onClick={getAiInsights} disabled={aiLoading}
                style={{ background: '#0F172A', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 18px', fontSize: '13px', fontWeight: '600', cursor: aiLoading ? 'wait' : 'pointer', whiteSpace: 'nowrap', marginLeft: '16px' }}>
                {aiLoading ? 'Analyzing…' : aiInsight ? '↻ Refresh insights' : '✦ Get AI insights'}
              </button>
            </div>
            {aiInsight && (
              <div style={{ fontSize: '13px', color: '#0F172A', lineHeight: '1.7', whiteSpace: 'pre-wrap', background: '#F8FAFC', borderRadius: '8px', padding: '16px', borderLeft: '3px solid #0F172A' }}>
                {aiInsight}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}